import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Copy, Check, Loader2, AlertTriangle, FileCode2, FileText, Link, Clock, ExternalLink, Eye, EyeOff, Cloud, CloudOff, Sparkles, Maximize2, X, Plus, Minus } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { useDarkMode } from '../contexts/DarkModeContext';
import { useShareLink } from '../contexts/ShareLinkContext';
import { sessionService } from '../services/sessionService';
import { useAuth } from '../services/AuthContext';
import { copyToClipboard } from '../utils/clipboard';
import { ModelPersistence } from '../services/openRouterService';
import { connectStream, getLatestModels, getMessages, startDescriptionCompressionSession } from '../services/agentService';
import {
  buildAuthenticatedSessionPdfUrl,
  buildPublicSessionPdfUrl,
  buildSharePdfUrl,
} from '../utils/pdfLinks';
import PdfBadgeIcon from './PdfBadgeIcon';

// YouTube description character limit
const YOUTUBE_DESCRIPTION_LIMIT = 5000;

// Decode HTML entities to display properly
const decodeHtmlEntities = (text: string): string => {
  if (!text) return '';
  return text
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&apos;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/&#(\d+);/g, (_, dec) => String.fromCharCode(dec))
    .replace(/&#x([0-9a-f]+);/gi, (_, hex) => String.fromCharCode(parseInt(hex, 16)));
};

interface TtmlSegment {
  begin: string;
  end: string;
  text: string;
  // Structured fields for topics
  topicRank?: string;
  topicTitle?: string;
  topicScore?: string;
  topicJustification?: string;
  // Structured fields for references
  refName?: string;
  refLinkText?: string;
  refUrl?: string;
  refType?: string;  // e.g., "Person", "Book", "Paper"
  refCategory?: string;  // grouping category
  refDescription?: string;
  // Original times (for edited mode - seeking uses original times)
  _originalBegin?: string;
  _originalEnd?: string;
}

interface DescriptionTabProps {
  sessionId: string;
  youtubeDescription?: string;
  ttmlContent?: string;
  sessionTitle?: string;
  linkedYoutubeUrl?: string;
  transcript?: string;
  onDescriptionChange?: (description: string) => void;
  onSeek?: (time: number) => void;
  forceReadOnly?: boolean;
}

// Target character limit for compression
const COMPRESSION_TARGET = 4500;

const resolveCompressionModel = async (): Promise<string | null> => {
  try {
    const latestModels = await getLatestModels();
    if (latestModels.opus) return latestModels.opus;
    if (latestModels.sonnet) return latestModels.sonnet;
  } catch {
    // Fall through to persisted user choice.
  }

  return ModelPersistence.getRefinementModel();
};

// Convert time string "HH:MM:SS" or "MM:SS" to seconds
const timeToSeconds = (timeStr: string): number => {
  if (!timeStr) return 0;
  const parts = timeStr.split(':').map(p => parseInt(p, 10));
  if (parts.length === 3) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  } else if (parts.length === 2) {
    return parts[0] * 60 + parts[1];
  }
  return 0;
};

// Parse time string like "00:01:30" or "1:30:00" to formatted time for YouTube
// Always outputs HH:MM:SS format
const formatTimeForYouTube = (timeStr: string): string => {
  if (!timeStr) return '00:00:00';

  const parts = timeStr.split(':').map(p => parseInt(p, 10));

  if (parts.length === 3) {
    // HH:MM:SS
    const [hours, minutes, seconds] = parts;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  } else if (parts.length === 2) {
    // MM:SS - add 00 hours
    const [minutes, seconds] = parts;
    return `00:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  return timeStr;
};

// Parse TTML content to extract topics and references
const parseTtmlContent = (content: string): { topics: TtmlSegment[]; references: TtmlSegment[] } => {
  if (!content) return { topics: [], references: [] };

  const topics: TtmlSegment[] = [];
  const references: TtmlSegment[] = [];

  // Parse topics from d_summary div
  const summaryDivRegex = /<div[^>]*xml:id="d_summary"[^>]*>([\s\S]*?)<\/div>/;
  const summaryMatch = content.match(summaryDivRegex);
  if (summaryMatch) {
    const pRegex = /<p\s+begin="([^"]+)"\s+end="([^"]+)"[^>]*>([\s\S]*?)<\/p>/g;
    let match;
    while ((match = pRegex.exec(summaryMatch[1])) !== null) {
      const rawText = match[3];
      // Extract topic rank and title from format: [1: 20/20] >> [Title] or [1: ?/9] >> [Title]
      const titleMatch = rawText.match(/\[(\d+):\s*[\d\/?]+\]\s*(?:&gt;&gt;|>>)\s*\[([^\]]+)\]/);

      // Extract justification - only the text between "Justification:" and "Optimal Placement:"
      // Format: Score: 8/10 (S: 3/4, ...) Justification: actual text here. Optimal Placement: [1/20] Continuity: ...
      let justification: string | undefined;
      if (titleMatch) {
        const afterTitle = rawText.substring(rawText.indexOf(titleMatch[0]) + titleMatch[0].length);
        // Clean up HTML first
        const cleanedText = afterTitle
          .replace(/<br\s*\/?>/gi, ' ')
          .replace(/<[^>]+>/g, '')
          .replace(/&amp;/g, '&')
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .trim();

        // Extract only the text between "Justification:" and "Optimal Placement:" (or end)
        const justificationMatch = cleanedText.match(/Justification:\s*(.+?)(?:\s*Optimal Placement:|$)/i);
        if (justificationMatch) {
          justification = justificationMatch[1].trim();
        } else {
          // Fallback: if no "Justification:" label, skip Score and take remaining text
          const withoutScore = cleanedText.replace(/^Score:\s*[\d\/]+\s*\([^)]+\)\s*/i, '').trim();
          justification = withoutScore.replace(/\s*Optimal Placement:.*$/i, '').trim();
        }

        if (!justification) justification = undefined;
      }

      topics.push({
        begin: match[1],
        end: match[2],
        text: rawText.replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]+>/g, '').trim(),
        topicRank: titleMatch ? titleMatch[1] : undefined,
        topicTitle: titleMatch ? decodeHtmlEntities(titleMatch[2]) : undefined,
        topicJustification: justification
      });
    }
  }

  // Parse references from d_references div
  const refDivRegex = /<div[^>]*xml:id="d_references"[^>]*>([\s\S]*?)<\/div>/;
  const refMatch = content.match(refDivRegex);
  if (refMatch) {
    const pRegex = /<p\s+begin="([^"]+)"\s+end="([^"]+)"[^>]*>([\s\S]*?)<\/p>/g;
    let match;
    while ((match = pRegex.exec(refMatch[1])) !== null) {
      const rawText = match[3];
      // Extract reference data
      // Format can be:
      // 1. Multi-line: Name<br/>Type<br/>Description
      // 2. Pipe format: [REF] Name | Site Type | URL: URL
      // 3. With markdown link: Name<br/>[Link Text](URL)<br/>Category: ...
      const lines = rawText.replace(/<br\s*\/?>/gi, '\n').split('\n').map(l => l.replace(/<[^>]+>/g, '').trim()).filter(l => l);

      let refName = '';
      let refUrl = '';
      let refLinkText = '';
      let refType = '';  // e.g., "Person", "Book", "Paper", "Company"
      let refCategory = '';  // grouping category from "Category:" field
      let refDescription = '';

      // Find URL in format [text](url) anywhere in the raw text
      const urlMatch = rawText.match(/\[([^\]]+)\]\(([^)]+)\)/);
      if (urlMatch) {
        refLinkText = urlMatch[1];
        refUrl = urlMatch[2];
      } else {
        // Check for raw URL
        const rawUrlMatch = rawText.match(/https?:\/\/[^\s<]+/);
        if (rawUrlMatch) {
          refUrl = rawUrlMatch[0];
        }
      }

      // Parse lines to extract structured data
      if (lines.length > 0) {
        let firstLine = lines[0];

        // Remove [REF] prefix if present
        firstLine = firstLine.replace(/^\[REF\]\s*/i, '');

        // Check for pipe format: "Name | Type | URL" or "Name | Link Text | URL"
        const pipeMatch = firstLine.match(/^([^|]+)\s*\|\s*([^|]+)(?:\s*\|\s*(.+))?$/);
        if (pipeMatch) {
          refName = pipeMatch[1].trim();
          // Second part could be type or link text
          const secondPart = pipeMatch[2].trim();
          if (!secondPart.startsWith('http')) {
            refType = secondPart;
          }
        } else {
          // Simple format - first line is the name (remove any URL suffix)
          refName = firstLine.replace(/:\s*https?:\/\/.*$/i, '').trim();
        }

        // Second line is typically the type (if it's a short word like "Person", "Book", etc.)
        if (lines.length > 1 && !lines[1].includes(':') && lines[1].length < 50) {
          const potentialType = lines[1].trim();
          // Common reference types
          const knownTypes = ['Person', 'Book', 'Paper', 'Company', 'Organization', 'Website', 'Article', 'Video', 'Podcast', 'Tool', 'Framework', 'Concept', 'Institution', 'Event', 'Software', 'Research', 'Project'];
          if (knownTypes.some(t => potentialType.toLowerCase().includes(t.toLowerCase())) || potentialType.length < 30) {
            refType = potentialType;
          }
        }

        // Find description - usually the longest line after name and type, or line with "Context:"
        const contextMatch = rawText.match(/Context:\s*([^\n<]+)/i);
        if (contextMatch) {
          refDescription = contextMatch[1].trim();
        } else {
          // Look for description in remaining lines (typically line 3 or later, or the longest line)
          const descLines = lines.slice(refType ? 2 : 1).filter(l =>
            !l.startsWith('Category:') &&
            !l.startsWith('Context:') &&
            !l.match(/^https?:\/\//) &&
            !l.match(/^\[.+\]\(.+\)$/) &&
            l.length > 10
          );
          if (descLines.length > 0) {
            // Take the longest line as description
            refDescription = descLines.reduce((a, b) => a.length > b.length ? a : b, '');
          }
        }
      }

      // Find category (for grouping)
      const categoryMatch = rawText.match(/Category:\s*([^\n<]+)/i);
      if (categoryMatch) {
        refCategory = categoryMatch[1].trim();
      }
      // If no explicit category, use refType as category
      if (!refCategory && refType) {
        refCategory = refType;
      }

      // Decode HTML entities in all fields
      refName = decodeHtmlEntities(refName);
      refLinkText = decodeHtmlEntities(refLinkText);
      refType = decodeHtmlEntities(refType);
      refCategory = decodeHtmlEntities(refCategory);
      refDescription = decodeHtmlEntities(refDescription);
      refUrl = decodeHtmlEntities(refUrl);

      if (refUrl || refName) {
        references.push({
          begin: match[1],
          end: match[2],
          text: decodeHtmlEntities(rawText.replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]+>/g, '').trim()),
          refName,
          refLinkText,
          refUrl,
          refType,
          refCategory,
          refDescription
        });
      }
    }
  }

  return { topics, references };
};

// Generate TOC from topics
const generateTOC = (topics: TtmlSegment[]): string => {
  if (topics.length === 0) return '';

  const lines: string[] = [];

  topics.forEach((topic) => {
    if (topic.topicTitle) {
      const time = formatTimeForYouTube(topic.begin);
      // Decode HTML entities for proper display in plain text
      lines.push(`${time} ${decodeHtmlEntities(topic.topicTitle)}`);
    }
  });

  return lines.join('\n');
};

// Generate references section - format: Category:\n[HH:MM:SS] Name\nURL
const generateReferences = (references: TtmlSegment[]): string => {
  if (references.length === 0) return '';

  // Group by category
  const byCategory: Record<string, TtmlSegment[]> = {};
  references.forEach(ref => {
    const category = ref.refCategory || 'General';
    if (!byCategory[category]) {
      byCategory[category] = [];
    }
    byCategory[category].push(ref);
  });

  const lines: string[] = [];

  Object.entries(byCategory).forEach(([category, refs]) => {
    lines.push(`${category}:`);
    refs.forEach(ref => {
      if (ref.refUrl) {
        // Output timestamp, name and URL
        const time = formatTimeForYouTube(ref.begin);
        lines.push(`[${time}] ${ref.refName || ref.refLinkText || 'Link'}`);
        lines.push(ref.refUrl);
      }
    });
  });

  return lines.join('\n').trim();
};

const DescriptionTab: React.FC<DescriptionTabProps> = ({
  sessionId,
  youtubeDescription = '',
  ttmlContent = '',
  sessionTitle = '',
  linkedYoutubeUrl,
  transcript = '',
  onDescriptionChange,
  onSeek,
  forceReadOnly = false,
}) => {
  const { isDarkMode } = useDarkMode();
  const { permissions: sharePermissions, shareToken } = useShareLink();
  const { token } = useAuth();

  const [description, setDescription] = useState(youtubeDescription);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied'>('idle');
  const [copyMarkdownStatus, setCopyMarkdownStatus] = useState<'idle' | 'copied'>('idle');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showJustification, setShowJustification] = useState(true);
  const [showPreview, setShowPreview] = useState(true);
  const [showSplitEditor, setShowSplitEditor] = useState(false);
  const [splitEditorTextSize, setSplitEditorTextSize] = useState<'sm' | 'base' | 'lg' | 'xl'>('base');

  // LLM generation state
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);
  const [compressEnabled, setCompressEnabled] = useState(false);
  const [llmError, setLlmError] = useState<string | null>(null);

  // Agent-based description generation state
  const [agentSessionId, setAgentSessionId] = useState<string | null>(null);
  const [agentProgress, setAgentProgress] = useState<string>('');
  const agentEventSourceRef = useRef<EventSource | null>(null);

  // Autosave timer ref
  const autosaveTimerRef = useRef<NodeJS.Timeout | null>(null);

  // PDF export state
  const [pdfStatus, setPdfStatus] = useState<'cached' | 'generating' | 'not-generated' | 'error'>('not-generated');
  const [pdfJobId, setPdfJobId] = useState<string | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [pdfLinkCopied, setPdfLinkCopied] = useState(false);

  // Share links state (for appending MLST share link)
  const [mlstShareUrl, setMlstShareUrl] = useState<string | null>(null);

  // API base URL for PDF endpoints
  const API_BASE_URL = process.env.NODE_ENV === 'production'
    ? '/api'
    : 'http://localhost:7777/api';

  // Base URL for constructing public links
  const PUBLIC_BASE_URL = process.env.NODE_ENV === 'production'
    ? window.location.origin
    : 'http://localhost:3000';

  // Parse TTML content
  const { topics: allTopics, references: allReferences } = useMemo(() => parseTtmlContent(ttmlContent), [ttmlContent]);
  const topics = allTopics;
  const references = allReferences;

  // Generate TOC and references
  const generatedTOC = useMemo(() => generateTOC(topics), [topics]);
  const generatedRefs = useMemo(() => generateReferences(references), [references]);

  // Group references by category for visual display
  const groupedReferences = useMemo(() => {
    const byCategory: Record<string, TtmlSegment[]> = {};
    references.forEach(ref => {
      const category = ref.refCategory || 'General';
      if (!byCategory[category]) {
        byCategory[category] = [];
      }
      byCategory[category].push(ref);
    });
    return byCategory;
  }, [references]);

  // Handle seeking to a timestamp
  const handleSeek = useCallback((timeStr: string) => {
    if (onSeek) {
      const seconds = timeToSeconds(timeStr);
      onSeek(seconds);
    }
  }, [onSeek]);

  // Track the original description from the database (what was loaded, not what user typed)
  const [savedDescription, setSavedDescription] = useState(youtubeDescription || '');

  // Flag to track if we're currently editing (to ignore prop updates from our own edits)
  const [isUserEditing, setIsUserEditing] = useState(false);

  // Clean up SSE connection on unmount
  useEffect(() => {
    return () => {
      if (agentEventSourceRef.current) {
        agentEventSourceRef.current.close();
        agentEventSourceRef.current = null;
      }
    };
  }, []);

  // Sync from props only on initial load (when savedDescription is first set)
  // After that, only update if the prop changes and we're NOT in the middle of editing
  useEffect(() => {
    // Skip if user is currently editing - the prop change is from our own callback
    if (isUserEditing) return;

    // Only sync if the prop actually changed from what we have saved
    if (youtubeDescription !== undefined && youtubeDescription !== savedDescription) {
      setDescription(youtubeDescription);
      setSavedDescription(youtubeDescription);
      setHasUnsavedChanges(false);
    }
  }, [youtubeDescription, savedDescription, isUserEditing]);

  // Check if user is a public share viewer (read-only)
  const isReadOnly = !!sharePermissions || forceReadOnly;
  const canUsePdfExport = !!sessionId && (!!token || !!shareToken);

  // Total character count for the full description (what will be copied)
  const fullDescriptionCharCount = useMemo(() => {
    const parts: string[] = [];
    if (description.trim()) {
      parts.push(description.trim());
    }
    if (generatedTOC) {
      parts.push('\n---\nTIMESTAMPS:');
      parts.push(generatedTOC);
    }
    if (generatedRefs) {
      parts.push('\n---\nREFERENCES:');
      parts.push(generatedRefs);
    }
    return parts.join('\n').length;
  }, [description, generatedTOC, generatedRefs]);
  const isOverLimit = fullDescriptionCharCount > YOUTUBE_DESCRIPTION_LIMIT;

  // Autosave function
  const performAutosave = useCallback(async (textToSave: string) => {
    if (!token || !sessionId) return;

    setIsSaving(true);
    setSaveStatus('saving');

    try {
      await sessionService.updateSession({
        sessionId,
        youtubeDescription: textToSave
      }, token);

      setSaveStatus('saved');
      setHasUnsavedChanges(false);
      setSavedDescription(textToSave);
      setIsUserEditing(false);

      // Reset status after 2 seconds
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Failed to autosave description:', error);
      setSaveStatus('error');
      // Keep error visible for 3 seconds
      setTimeout(() => setSaveStatus('idle'), 3000);
    } finally {
      setIsSaving(false);
    }
  }, [token, sessionId]);

  // Handle description change with debounced autosave
  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setIsUserEditing(true);
    setDescription(newValue);
    setHasUnsavedChanges(true);
    onDescriptionChange?.(newValue);

    // Clear existing autosave timer
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
    }

    // Set new autosave timer (1.5 second debounce)
    autosaveTimerRef.current = setTimeout(() => {
      performAutosave(newValue);
    }, 1500);
  };

  // Cleanup autosave timer on unmount
  useEffect(() => {
    return () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
      }
    };
  }, []);

  // Handle Escape key to close split editor
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showSplitEditor) {
        setShowSplitEditor(false);
      }
    };

    if (showSplitEditor) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [showSplitEditor]);

  // Track previous TTML content to detect actual changes
  const prevTtmlContentRef = useRef<string | null>(null);

  // Fetch MLST share link for this session
  useEffect(() => {
    const fetchMlstShareLink = async () => {
      if (!sessionId || !token) return;

      try {
        const response = await fetch(`${API_BASE_URL}/sessions/${sessionId}/share-links`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!response.ok) return;

        const links = await response.json();
        // Find the share link with label "MLST" (case-insensitive)
        const mlstLink = links.find((link: any) =>
          link.label?.toLowerCase() === 'mlst' && link.isEnabled && link.status === 'active'
        );
        if (mlstLink?.url) {
          setMlstShareUrl(mlstLink.url);
        }
      } catch (error) {
        console.error('[Description] Error fetching share links:', error);
      }
    };

    fetchMlstShareLink();
  }, [sessionId, token, API_BASE_URL]);

  // Check PDF status on mount and when ttmlContent changes
  useEffect(() => {
    if (!sessionId) {
      if (prevTtmlContentRef.current !== null) {
        setPdfStatus('not-generated');
        setPdfJobId(null);
        setPdfError(null);
        prevTtmlContentRef.current = null;
      }
      return;
    }

    if (!token) {
      return;
    }

    // If TTML content actually changed, reset state
    // Use length as a proxy to avoid comparing large strings
    const ttmlKey = `${sessionId}-${ttmlContent.length}`;
    if (prevTtmlContentRef.current !== ttmlKey) {
      setPdfStatus('not-generated');
      setPdfJobId(null);
      setPdfError(null);
      prevTtmlContentRef.current = ttmlKey;
    }

    const checkPdfStatus = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/sessions/${sessionId}/pdf/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!response.ok) {
          return;
        }

        const data = await response.json();
        if (data.status === 'cached' || data.status === 'generating') {
          setPdfStatus(data.status);
          if (data.jobId) setPdfJobId(data.jobId);
        }
      } catch (error) {
        console.error('[PDF] Error checking status:', error);
      }
    };

    checkPdfStatus();
  }, [sessionId, token, ttmlContent]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll for PDF job completion
  useEffect(() => {
    if (!pdfJobId || pdfStatus !== 'generating' || !sessionId || !token) return;

    const pollInterval = setInterval(async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/sessions/${sessionId}/pdf/jobs/${pdfJobId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!response.ok) return;

        const data = await response.json();

        if (data.status === 'completed') {
          setPdfStatus('cached');
          setPdfJobId(null);
        } else if (data.status === 'failed') {
          setPdfStatus('error');
          setPdfError(data.error || 'PDF generation failed');
          setPdfJobId(null);
        }
      } catch (error) {
        console.error('[PDF] Error polling job status:', error);
      }
    }, 2000);

    return () => clearInterval(pollInterval);
  }, [pdfJobId, pdfStatus, sessionId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const pdfActionLink = useMemo(() => {
    if (!sessionId) return null;
    if (shareToken && !token) {
      return buildSharePdfUrl(shareToken, API_BASE_URL, PUBLIC_BASE_URL);
    }
    return buildAuthenticatedSessionPdfUrl(sessionId, API_BASE_URL);
  }, [sessionId, shareToken, token, API_BASE_URL, PUBLIC_BASE_URL]);

  const pdfPublicLink = useMemo(() => {
    if (!sessionId) return null;
    return buildPublicSessionPdfUrl(sessionId, PUBLIC_BASE_URL);
  }, [sessionId, PUBLIC_BASE_URL]);

  // Handle PDF download/generation
  const handleDownloadPDF = async () => {
    if (!sessionId || (!token && !shareToken) || !pdfActionLink) return;

    if (shareToken && !token) {
      window.open(pdfActionLink, '_blank', 'noopener');
      return;
    }

    if (pdfStatus === 'cached') {
      window.open(pdfActionLink, '_blank', 'noopener');
      return;
    }

    // Prevent duplicate calls while generating
    if (pdfStatus === 'generating') return;

    // Trigger generation
    setPdfStatus('generating');
    setPdfError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/sessions/${sessionId}/pdf/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ force: false })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to start PDF generation');
      }

      const data = await response.json();

      if (data.status === 'cached') {
        setPdfStatus('cached');
        window.open(pdfActionLink, '_blank', 'noopener');
      } else {
        setPdfJobId(data.jobId);
      }
    } catch (error) {
      console.error('[PDF] Error generating PDF:', error);
      setPdfStatus('error');
      setPdfError(error instanceof Error ? error.message : 'Failed to generate PDF');
    }
  };

  const pdfActionTitle = pdfStatus === 'error'
    ? pdfError || 'PDF export unavailable'
    : pdfStatus === 'generating'
      ? 'Generating PDF transcript...'
      : pdfStatus === 'cached'
        ? 'Download PDF transcript'
        : 'Generate PDF transcript';

  // Copy PDF link to clipboard
  const handleCopyPdfLink = async () => {
    const linkToCopy = pdfPublicLink || pdfActionLink;
    if (!linkToCopy) return;
    const success = await copyToClipboard(linkToCopy);
    if (success) {
      setPdfLinkCopied(true);
      setTimeout(() => setPdfLinkCopied(false), 2000);
    }
  };

  // Build full YouTube description for copy
  const buildFullDescription = useCallback((): string => {
    const parts: string[] = [];

    // Add user description
    if (description.trim()) {
      parts.push(description.trim());
    }

    // Add TOC if available
    if (generatedTOC) {
      parts.push('\n---\nTIMESTAMPS:');
      parts.push(generatedTOC);
    }

    // Add references if available
    if (generatedRefs) {
      parts.push('\n---\nREFERENCES:');
      parts.push(generatedRefs);
    }

    // Add links section (PDF and MLST share)
    const links: string[] = [];
    if ((token || shareToken) && pdfPublicLink) {
      links.push(`Download PDF transcript: ${pdfPublicLink}`);
    }
    if (mlstShareUrl) {
      links.push(`Full Transcript: ${mlstShareUrl}`);
    }
    if (links.length > 0) {
      parts.push('\n---\nLINKS:');
      parts.push(links.join('\n'));
    }

    return parts.join('\n');
  }, [description, generatedTOC, generatedRefs, pdfPublicLink, mlstShareUrl, shareToken, token]);

  // Copy full description to clipboard (with optional compression)
  const handleCopy = async () => {
    setLlmError(null);

    let textToCopy: string;

    // If compress is enabled, compress before copying
    if (compressEnabled) {
      setIsCompressing(true);
      try {
        const compressed = await compressDescription();
        if (compressed) {
          textToCopy = compressed;
        } else {
          throw new Error('Compression returned empty result');
        }
      } catch (error) {
        setLlmError(error instanceof Error ? error.message : 'Failed to compress description');
        setIsCompressing(false);
        return;
      }
      setIsCompressing(false);
    } else {
      textToCopy = buildFullDescription();
    }

    const success = await copyToClipboard(textToCopy);

    if (success) {
      setCopyStatus('copied');
      setTimeout(() => setCopyStatus('idle'), 2000);
    }
  };

  // Build markdown-formatted description for YouTube
  const buildMarkdownDescription = useCallback((): string => {
    const parts: string[] = [];

    // Add user description (already supports markdown)
    if (description.trim()) {
      parts.push(description.trim());
    }

    // Add TOC with markdown formatting - include justifications/descriptions
    if (topics.length > 0) {
      parts.push('\n---\n## Timestamps\n');
      topics.forEach((topic) => {
        if (topic.topicTitle) {
          const time = formatTimeForYouTube(topic.begin);
          // Decode HTML entities for proper display
          const title = decodeHtmlEntities(topic.topicTitle);
          parts.push(`**${time}** - ${title}`);
          // Include justification/description if available
          if (topic.topicJustification) {
            parts.push(`> ${decodeHtmlEntities(topic.topicJustification)}`);
          }
          parts.push(''); // Empty line between entries
        }
      });
    }

    // Add references with markdown formatting - grouped by category with timestamps
    if (references.length > 0) {
      parts.push('\n---\n## References\n');

      // Group by category
      const byCategory: Record<string, typeof references> = {};
      references.forEach(ref => {
        const category = ref.refCategory || 'General';
        if (!byCategory[category]) {
          byCategory[category] = [];
        }
        byCategory[category].push(ref);
      });

      Object.entries(byCategory).forEach(([category, refs]) => {
        parts.push(`### ${category}\n`);
        refs.forEach(ref => {
          const name = ref.refName || ref.refLinkText || 'Link';
          const time = formatTimeForYouTube(ref.begin);
          const typeTag = ref.refType ? ` *(${ref.refType})*` : '';
          if (ref.refUrl) {
            parts.push(`- **[${time}]** [${name}](${ref.refUrl})${typeTag}`);
          } else {
            parts.push(`- **[${time}]** ${name}${typeTag}`);
          }
          // Include description if available
          if (ref.refDescription) {
            parts.push(`  > ${ref.refDescription}`);
          }
        });
        parts.push(''); // Empty line between categories
      });
    }

    return parts.join('\n');
  }, [description, topics, references]);

  // Copy as Markdown
  const handleCopyMarkdown = async () => {
    const markdownDescription = buildMarkdownDescription();
    const success = await copyToClipboard(markdownDescription);

    if (success) {
      setCopyMarkdownStatus('copied');
      setTimeout(() => setCopyMarkdownStatus('idle'), 2000);
    }
  };

  // Generate description using server-side agent
  const handleGenerateSummary = async () => {
    if (!sessionId || isGeneratingSummary) return;

    setIsGeneratingSummary(true);
    setLlmError(null);
    setAgentProgress('Starting agent...');

    try {
      // Create agent session for description generation
      const response = await fetch(`${API_BASE_URL}/v1/agent/workflows/description`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ sessionId })
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Failed to start agent' }));
        throw new Error(err.error || 'Failed to start description generation');
      }

      const result = await response.json();
      const newAgentSessionId = result.agentSessionId;
      setAgentSessionId(newAgentSessionId);

      // Connect to SSE stream for progress updates
      const eventSource = connectStream(
        newAgentSessionId,
        (data) => {
          if (data.type === 'text_delta' || data.type === 'text') {
            setAgentProgress(data.content || data.message || 'Working...');
          } else if (data.type === 'tool_start') {
            setAgentProgress(`Calling ${data.tool || 'API'}...`);
          } else if (data.type === 'done' || data.type === 'complete') {
            eventSource.close();
            agentEventSourceRef.current = null;
            setAgentProgress('');
            setIsGeneratingSummary(false);
            window.dispatchEvent(new CustomEvent('rescript-session-refresh-needed', {
              detail: { sessionId, scopes: ['description'] },
            }));

            fetch(`${API_BASE_URL}/sessions/${sessionId}`, {
              headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            })
              .then(r => r.json())
              .then(session => {
                if (session.youtube_description) {
                  setDescription(session.youtube_description);
                  setSavedDescription(session.youtube_description);
                  setHasUnsavedChanges(false);
                  onDescriptionChange?.(session.youtube_description);
                }
              })
              .catch(() => {});
          } else if (data.type === 'error') {
            eventSource.close();
            agentEventSourceRef.current = null;
            setAgentProgress('');
            setIsGeneratingSummary(false);
            setLlmError(data.message || 'Agent error');
          }
        },
        () => {
          eventSource.close();
          agentEventSourceRef.current = null;
          setAgentProgress('');
          setIsGeneratingSummary(false);
        },
        token || undefined
      );
      agentEventSourceRef.current = eventSource;
    } catch (error) {
      console.error('Error starting description generation:', error);
      setLlmError(error instanceof Error ? error.message : 'Failed to start description generation');
      setAgentProgress('');
      setIsGeneratingSummary(false);
    }
  };

  // Compress description using Agent SDK (server-side) - returns compressed text, does NOT modify state
  const compressDescription = async (): Promise<string | null> => {
    const fullDescription = buildFullDescription();
    if (fullDescription.length <= COMPRESSION_TARGET) {
      // Already within limit, no compression needed
      return fullDescription;
    }

    try {
      const refinementModel = await resolveCompressionModel();
      if (!refinementModel) {
        throw new Error('No compression model is available right now');
      }

      const result = await startDescriptionCompressionSession({
        sessionId,
        description,
        toc: generatedTOC,
        references: generatedRefs,
        targetLength: COMPRESSION_TARGET,
        model: refinementModel,
      }, token || undefined);

      setAgentSessionId(result.agentSessionId);
      setAgentProgress('Starting compression agent...');

      const compressedDescription = await new Promise<string>((resolve, reject) => {
        let finished = false;
        let eventSource: EventSource | null = null;
        const fail = (error: unknown) => {
          if (finished) return;
          finished = true;
          if (agentEventSourceRef.current === eventSource) {
            agentEventSourceRef.current = null;
          }
          eventSource?.close();
          setAgentProgress('');
          reject(error instanceof Error ? error : new Error('Failed to compress description'));
        };

        const finalize = async () => {
          if (finished) return;
          finished = true;
          eventSource?.close();
          if (agentEventSourceRef.current === eventSource) {
            agentEventSourceRef.current = null;
          }
          setAgentProgress('');
          try {
            const messages = await getMessages(result.agentSessionId, token || undefined);
            const assistantMessages = messages.filter((message) => message.role === 'assistant');
            const finalMessage = assistantMessages[assistantMessages.length - 1]?.content?.trim();
            if (!finalMessage) {
              throw new Error('Compression agent did not return any output');
            }
            resolve(finalMessage);
          } catch (error) {
            fail(error);
          }
        };

        eventSource = connectStream(
          result.agentSessionId,
          (data) => {
            if (finished) return;
            if (data.type === 'tool_start') {
              setAgentProgress(`Running ${data.tool || 'tool'}...`);
            } else if (data.type === 'text_delta' || data.type === 'text') {
              setAgentProgress('Compressing description...');
            } else if (data.type === 'done' || data.type === 'complete') {
              void finalize();
            } else if (data.type === 'error') {
              fail(new Error(data.message || 'Compression agent failed'));
            }
          },
          () => fail(new Error('Compression agent stream disconnected')),
          token || undefined
        );

        agentEventSourceRef.current = eventSource;
      });

      return compressedDescription;
    } catch (error) {
      console.error('Error compressing description:', error);
      throw error;
    }
  };

  const headerIconButtonClassName = `inline-flex h-11 w-11 items-center justify-center rounded-xl transition-colors ${
    isDarkMode
      ? 'bg-[#33435a] text-[#F4F4F4] hover:bg-[#41516d]'
      : 'bg-[#232E3F] text-[#F4F4F4] hover:bg-[#1b2433]'
  }`;

  return (
    <div className={`h-full flex flex-col overflow-hidden ${isDarkMode ? 'bg-[#131a25]' : 'bg-[#f4f4f4]'}`}>
      {/* Header */}
      <div className={`flex-shrink-0 p-4 border-b ${isDarkMode ? 'border-[#334055] bg-[#202b3d]' : 'border-[#d9dde4] bg-[#faf7ef]'}`}>
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-[#314159]' : 'bg-[#fff1cc]'}`}>
              <FileText className={`w-5 h-5 ${isDarkMode ? 'text-[#ffcf74]' : 'text-[#232E3F]'}`} />
            </div>
            <div>
              <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                YouTube Description
              </h2>
              <p className={`hidden text-sm md:block ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {isReadOnly ? 'View the saved description, timestamps, and references' : 'Edit the description, timestamps, references, and optional PDF link'}
              </p>
            </div>
          </div>

          {/* Action buttons - only show for authenticated users */}
          {!isReadOnly && (
            <div className="flex flex-wrap items-center gap-2 xl:justify-end">
              {/* Copy for YouTube group (with optional compress) */}
              <div className="flex flex-wrap items-center gap-2">
                {/* Compress checkbox - shown when over limit, affects Copy for YouTube only */}
                {isOverLimit && (
                  <label
                    className={`flex items-center gap-1.5 cursor-pointer text-sm px-2 py-1.5 rounded-lg transition-colors ${
                      compressEnabled
                        ? isDarkMode ? 'bg-[#4b3913]/70 text-[#ffe09b]' : 'bg-[#fff1cc] text-[#6e4d0e]'
                        : isDarkMode ? 'bg-[#2d3749] text-[#d3d9e4]' : 'bg-[#edf0f4] text-[#4f5d72]'
                    }`}
                    title={`When enabled, compresses description to ~${COMPRESSION_TARGET} chars before copying`}
                  >
                    <input
                      type="checkbox"
                      checked={compressEnabled}
                      onChange={(e) => setCompressEnabled(e.target.checked)}
                      className="w-3.5 h-3.5 text-[#232E3F] border-gray-300 rounded focus:ring-[#ffcf74]"
                    />
                    <span>Compress</span>
                  </label>
                )}
                {/* Copy for YouTube button */}
                <button
                  onClick={handleCopy}
                  disabled={isCompressing}
                  className={`${headerIconButtonClassName} ${
                    isCompressing
                      ? isDarkMode ? 'bg-[#4b3913]/70 text-[#ffe09b]' : 'bg-[#fff1cc] text-[#6e4d0e]'
                      : copyStatus === 'copied'
                        ? 'bg-[#232E3F] text-[#ffcf74]'
                        : ''
                  }`}
                  title={compressEnabled ? `Copy compressed (~${COMPRESSION_TARGET} chars) for YouTube` : 'Copy plain text for YouTube description'}
                  aria-label={compressEnabled ? `Copy compressed (~${COMPRESSION_TARGET} chars) for YouTube` : 'Copy plain text for YouTube description'}
                >
                  {isCompressing ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : copyStatus === 'copied' ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <Copy className="w-5 h-5" />
                  )}
                </button>
              </div>

              {/* Copy as Markdown button (full uncompressed version) */}
              <button
                onClick={handleCopyMarkdown}
                className={`${headerIconButtonClassName} ${
                  copyMarkdownStatus === 'copied'
                    ? 'bg-[#232E3F] text-[#ffcf74]'
                    : isDarkMode
                      ? 'bg-[#33435a] hover:bg-[#41516d] text-white'
                      : 'bg-[#232E3F] hover:bg-[#1b2433] text-white'
                }`}
                title="Copy full markdown with descriptions (not compressed)"
                aria-label="Copy full markdown with descriptions"
              >
                {copyMarkdownStatus === 'copied' ? (
                  <Check className="w-5 h-5" />
                ) : (
                  <FileCode2 className="w-5 h-5" />
                )}
              </button>

              {/* PDF action and link copy */}
              {canUsePdfExport && (
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={handleDownloadPDF}
                    disabled={pdfStatus === 'generating'}
                    title={pdfActionTitle}
                    aria-label={pdfActionTitle}
                    className={`relative inline-flex h-11 w-11 items-center justify-center rounded-xl transition-opacity ${
                      pdfStatus === 'generating'
                        ? 'cursor-wait opacity-70'
                        : 'hover:opacity-80'
                    }`}
                  >
                    <PdfBadgeIcon isDarkMode={isDarkMode} />
                    {pdfStatus === 'generating' && (
                      <span className={`absolute -right-1 -top-1 rounded-full p-1 text-white shadow-sm ${
                        isDarkMode ? 'bg-gray-800' : 'bg-gray-900'
                      }`}>
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      </span>
                    )}
                  </button>
                  {/* Copy PDF link button */}
                  {(pdfPublicLink || pdfActionLink) && (
                    <button
                      onClick={handleCopyPdfLink}
                      title="Copy PDF download link"
                      className={`p-1.5 rounded-lg transition-all ${
                        pdfLinkCopied
                          ? 'text-[#ffcf74]'
                          : isDarkMode
                            ? 'text-gray-400 hover:bg-gray-700 hover:text-gray-200'
                            : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                      }`}
                    >
                      {pdfLinkCopied ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <Link className="w-4 h-4" />
                      )}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Description Editor/Viewer */}
          <div className={`rounded-xl border ${isDarkMode ? 'border-[#334055] bg-[#1d2636]' : 'border-[#d9dde4] bg-white/95'}`}>
            <div className={`px-4 py-3 border-b ${isDarkMode ? 'border-[#334055] bg-[#202b3d]' : 'border-[#e3e6eb] bg-[#faf7ef]'}`}>
              <div className="flex items-center justify-between">
                <h3 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Description
                </h3>

                {/* Toolbar for authenticated users */}
                {!isReadOnly && (
                  <div className="flex items-center">
                    {/* Preview toggle */}
                    <label className={`flex items-center gap-1.5 cursor-pointer text-sm px-3 py-1 rounded-md transition-colors ${
                      showPreview
                        ? isDarkMode ? 'bg-[#4b3913]/70 text-[#ffe09b]' : 'bg-[#fff1cc] text-[#6e4d0e]'
                        : isDarkMode ? 'text-gray-400 hover:bg-[#2d3749]' : 'text-gray-600 hover:bg-[#edf0f4]'
                    }`}>
                      <input
                        type="checkbox"
                        checked={showPreview}
                        onChange={(e) => setShowPreview(e.target.checked)}
                        className="sr-only"
                      />
                      <Eye className="w-3.5 h-3.5" />
                      <span>Preview</span>
                    </label>

                    {/* Fullscreen split editor button */}
                    <button
                      onClick={() => setShowSplitEditor(true)}
                      className={`flex items-center gap-1.5 text-sm px-3 py-1 rounded-md transition-colors ml-1 ${
                        isDarkMode ? 'text-gray-400 hover:bg-gray-700 hover:text-gray-200' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
                      }`}
                      title="Open fullscreen split editor"
                    >
                      <Maximize2 className="w-3.5 h-3.5" />
                      <span>Fullscreen</span>
                    </button>

                    {/* Divider */}
                    <div className={`mx-2 h-5 w-px ${isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`} />

                    {/* Character count with progress bar */}
                    <div className="flex items-center gap-2">
                      <div className="flex flex-col items-end">
                        <span className={`text-xs ${
                          isOverLimit
                            ? 'text-red-500 font-medium'
                            : isDarkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          {fullDescriptionCharCount.toLocaleString()} / {YOUTUBE_DESCRIPTION_LIMIT.toLocaleString()}
                        </span>
                        <div className={`h-1 w-20 rounded-full overflow-hidden ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                          <div
                            className={`h-full transition-all ${
                              isOverLimit ? 'bg-red-500' :
                              fullDescriptionCharCount / YOUTUBE_DESCRIPTION_LIMIT > 0.8 ? 'bg-yellow-500' :
                              'bg-[#232E3F]'
                            }`}
                            style={{ width: `${Math.min(100, (fullDescriptionCharCount / YOUTUBE_DESCRIPTION_LIMIT) * 100)}%` }}
                          />
                        </div>
                      </div>
                      {isOverLimit && (
                        <AlertTriangle className="w-4 h-4 text-red-500" />
                      )}
                    </div>

                    {/* Divider */}
                    <div className={`mx-2 h-5 w-px ${isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`} />

                    {/* Autosave status indicator */}
                    <div className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded-md ${
                      saveStatus === 'saving'
                        ? isDarkMode ? 'bg-[#314159] text-[#d9e2f1]' : 'bg-[#edf0f4] text-[#455468]'
                        : saveStatus === 'saved'
                          ? isDarkMode ? 'bg-[#223042] text-[#ffcf74]' : 'bg-[#232E3F] text-[#ffcf74]'
                          : saveStatus === 'error'
                            ? isDarkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-700'
                            : hasUnsavedChanges
                              ? isDarkMode ? 'bg-[#4b3913]/70 text-[#ffe09b]' : 'bg-[#fff1cc] text-[#6e4d0e]'
                              : isDarkMode ? 'bg-[#2d3749] text-gray-400' : 'bg-[#edf0f4] text-gray-500'
                    }`}>
                      {saveStatus === 'saving' ? (
                        <>
                          <Loader2 className="w-3 h-3 animate-spin" />
                          <span>Saving</span>
                        </>
                      ) : saveStatus === 'saved' ? (
                        <>
                          <Cloud className="w-3 h-3" />
                          <span>Saved</span>
                        </>
                      ) : saveStatus === 'error' ? (
                        <>
                          <CloudOff className="w-3 h-3" />
                          <span>Error</span>
                        </>
                      ) : hasUnsavedChanges ? (
                        <>
                          <Cloud className="w-3 h-3" />
                          <span>Unsaved</span>
                        </>
                      ) : (
                        <>
                          <Cloud className="w-3 h-3" />
                          <span>Synced</span>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="p-4">
              {isReadOnly || showPreview ? (
                // Markdown rendered view for public share users or preview mode
                <div className={`prose prose-sm max-w-none ${
                  isDarkMode
                    ? 'prose-invert prose-p:text-gray-300 prose-headings:text-white prose-a:text-[#ffcf74] prose-strong:text-white prose-li:text-gray-300'
                    : 'prose-p:text-gray-700 prose-headings:text-gray-900 prose-strong:text-gray-900 prose-li:text-gray-700'
                }`}>
                  {description ? (
                    <ReactMarkdown>{description}</ReactMarkdown>
                  ) : (
                    <span className="italic text-gray-500">No description provided</span>
                  )}
                </div>
              ) : (
                // Editable textarea for authenticated users
                <textarea
                  value={description}
                  onChange={handleDescriptionChange}
                  placeholder="Write your YouTube video description here...&#10;&#10;Tips:&#10;- Start with a hook to grab attention&#10;- Include relevant keywords&#10;- Add links to mentioned resources&#10;- The TOC and References below will be appended automatically when copying&#10;- Markdown formatting is supported"
                  className={`w-full h-64 p-3 rounded-lg border resize-none font-mono text-sm ${
                    isDarkMode
                      ? 'bg-[#131a25] border-[#40506a] text-gray-200 placeholder-gray-500 focus:border-[#ffcf74]'
                      : 'bg-[#fbfaf7] border-[#cfd5df] text-gray-800 placeholder-gray-400 focus:border-[#ffcf74]'
                  } focus:outline-none focus:ring-2 focus:ring-[#ffcf74]/20`}
                />
              )}

              {/* LLM Action Buttons - only for authenticated users */}
              {!isReadOnly && (
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  {/* Generate Description button (agent-based) */}
                  <button
                    onClick={handleGenerateSummary}
                    disabled={isGeneratingSummary}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg font-medium text-sm transition-colors ${
                    isGeneratingSummary
                      ? isDarkMode ? 'bg-[#314159] text-[#d9e2f1]' : 'bg-[#edf0f4] text-[#455468]'
                      : isDarkMode
                        ? 'bg-[#FFB52B] hover:bg-[#f0a61d] text-[#232E3F] disabled:bg-[#2d3749] disabled:text-gray-500'
                        : 'bg-[#FFB52B] hover:bg-[#f0a61d] text-[#232E3F] disabled:bg-gray-200 disabled:text-gray-400'
                  } disabled:cursor-not-allowed`}
                    title="Generate a YouTube description using the AI agent"
                  >
                    {isGeneratingSummary ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {agentProgress || 'Generating...'}
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4" />
                        Generate Description
                      </>
                    )}
                  </button>

                  {/* Error message */}
                  {llmError && (
                    <div className={`flex items-center gap-2 px-3 py-1 rounded-lg text-sm ${
                      isDarkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-700'
                    }`}>
                      <AlertTriangle className="w-4 h-4" />
                      {llmError}
                      <button
                        onClick={() => setLlmError(null)}
                        className="ml-1 hover:opacity-70"
                      >
                        ×
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Auto-generated TOC */}
          {topics.length > 0 && (
            <div className={`rounded-xl border ${isDarkMode ? 'border-[#334055] bg-[#1d2636]' : 'border-[#d9dde4] bg-white/95'}`}>
              <div className={`px-4 py-3 border-b ${isDarkMode ? 'border-[#334055] bg-[#202b3d]' : 'border-[#e3e6eb] bg-[#faf7ef]'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock className={`w-4 h-4 ${isDarkMode ? 'text-[#ffcf74]' : 'text-[#8a6115]'}`} />
                    <h3 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      Timestamps (Auto-generated from TTML)
                    </h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      isDarkMode ? 'bg-[#4b3913]/70 text-[#ffe09b]' : 'bg-[#fff1cc] text-[#6e4d0e]'
                    }`}>
                      {topics.length} topics
                    </span>
                  </div>
                  {/* Toggle justification visibility */}
                  <button
                    onClick={() => setShowJustification(!showJustification)}
                    className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs transition-colors ${
                      isDarkMode
                        ? 'hover:bg-gray-700 text-gray-400'
                        : 'hover:bg-gray-100 text-gray-500'
                    }`}
                    title={showJustification ? 'Hide justifications' : 'Show justifications'}
                  >
                    {showJustification ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    <span>{showJustification ? 'Hide details' : 'Show details'}</span>
                  </button>
                </div>
              </div>

              <div className="p-4 space-y-2">
                {topics.map((topic, index) => topic.topicTitle && (
                  <div key={index} className="space-y-1">
                    <div className="flex items-center gap-2 font-mono text-sm">
                      <button
                        onClick={() => handleSeek(topic.begin)}
                        className={`px-2 py-0.5 rounded font-medium transition-colors ${
                          isDarkMode
                            ? 'bg-[#4b3913]/60 text-[#ffe09b] hover:bg-[#5a4417]'
                            : 'bg-[#fff1cc] text-[#6e4d0e] hover:bg-[#ffe4a3]'
                        }`}
                      >
                        {formatTimeForYouTube(topic.begin)}
                      </button>
                      <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                        {decodeHtmlEntities(topic.topicTitle || '')}
                      </span>
                    </div>
                    {showJustification && topic.topicJustification && (
                      <p className={`text-sm italic ml-[6.5rem] ${
                        isDarkMode ? 'text-gray-500' : 'text-gray-500'
                      }`}>
                        {decodeHtmlEntities(topic.topicJustification)}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Auto-generated References - Grouped by Category */}
          {references.length > 0 && (
            <div className={`rounded-xl border ${isDarkMode ? 'border-[#334055] bg-[#1d2636]' : 'border-[#d9dde4] bg-white/95'}`}>
              <div className={`px-4 py-3 border-b ${isDarkMode ? 'border-[#334055] bg-[#202b3d]' : 'border-[#e3e6eb] bg-[#faf7ef]'}`}>
                <div className="flex items-center gap-2">
                  <Link className={`w-4 h-4 ${isDarkMode ? 'text-[#ffcf74]' : 'text-[#8a6115]'}`} />
                  <h3 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    References (Auto-generated from TTML)
                  </h3>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    isDarkMode ? 'bg-[#314159] text-[#d9e2f1]' : 'bg-[#edf0f4] text-[#455468]'
                  }`}>
                    {references.length} links
                  </span>
                </div>
              </div>

              <div className="p-4 space-y-4">
                {Object.entries(groupedReferences).map(([category, refs]) => (
                  <div key={category}>
                    {/* Category header */}
                    <h4 className={`text-sm font-semibold mb-2 ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {decodeHtmlEntities(category)}
                    </h4>
                    <div className="space-y-3 ml-2">
                      {refs.map((ref, index) => {
                        // Use parsed refDescription, fallback to extracting from text
                        const description = ref.refDescription || (() => {
                          const contextMatch = ref.text?.match(/Context:\s*(.+)/i);
                          return contextMatch ? contextMatch[1].trim() : null;
                        })();

                        return (
                          <div key={index} className="space-y-1">
                            <div className="flex items-start gap-2">
                              <button
                                onClick={() => handleSeek(ref.begin)}
                                className={`px-1.5 py-0.5 rounded font-mono text-xs font-medium transition-colors flex-shrink-0 ${
                                  isDarkMode
                                    ? 'bg-[#4b3913]/60 text-[#ffe09b] hover:bg-[#5a4417]'
                                    : 'bg-[#fff1cc] text-[#6e4d0e] hover:bg-[#ffe4a3]'
                                }`}
                              >
                                {formatTimeForYouTube(ref.begin)}
                              </button>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  {ref.refUrl ? (
                                    <a
                                      href={ref.refUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`hover:underline ${
                                        isDarkMode ? 'text-[#ffcf74]' : 'text-[#8a6115]'
                                      }`}
                                    >
                                      {ref.refName || ref.refLinkText || 'Link'}
                                    </a>
                                  ) : (
                                    <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                                      {ref.refName || ref.refLinkText || 'Reference'}
                                    </span>
                                  )}
                                  {ref.refUrl && (
                                    <ExternalLink className={`w-3 h-3 flex-shrink-0 ${
                                      isDarkMode ? 'text-gray-500' : 'text-gray-400'
                                    }`} />
                                  )}
                                  {/* Type chip */}
                                  {ref.refType && (
                                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                                      isDarkMode
                                        ? 'bg-[#314159] text-[#d9e2f1]'
                                        : 'bg-[#edf0f4] text-[#455468]'
                                    }`}>
                                      {ref.refType}
                                    </span>
                                  )}
                                </div>
                                {/* Description */}
                                {description && (
                                  <p className={`text-sm mt-1 ${
                                    isDarkMode ? 'text-gray-400' : 'text-gray-600'
                                  }`}>
                                    {description}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Fullscreen Split-Screen Editor Modal */}
      {showSplitEditor && (
        <div className="fixed inset-0 z-50 flex flex-col">
          {/* Modal Header */}
          <div className={`flex-shrink-0 p-4 border-b flex items-center justify-between ${
            isDarkMode ? 'bg-[#202b3d] border-[#334055]' : 'bg-[#faf7ef] border-[#d9dde4]'
          }`}>
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-[#314159]' : 'bg-[#fff1cc]'}`}>
                <FileText className={`w-5 h-5 ${isDarkMode ? 'text-[#ffcf74]' : 'text-[#232E3F]'}`} />
              </div>
              <div>
                <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Split-Screen Editor
                </h2>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Edit on the left, preview on the right
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Text size controls */}
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setSplitEditorTextSize(prev => {
                    const sizes: ('sm' | 'base' | 'lg' | 'xl')[] = ['sm', 'base', 'lg', 'xl'];
                    const idx = sizes.indexOf(prev);
                    return idx > 0 ? sizes[idx - 1] : prev;
                  })}
                  disabled={splitEditorTextSize === 'sm'}
                  className={`p-1.5 rounded transition-colors ${
                    splitEditorTextSize === 'sm'
                      ? isDarkMode ? 'text-gray-600 cursor-not-allowed' : 'text-gray-300 cursor-not-allowed'
                      : isDarkMode ? 'text-gray-400 hover:bg-gray-700 hover:text-white' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                  }`}
                  title="Decrease text size"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className={`text-xs px-2 min-w-[2.5rem] text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {splitEditorTextSize.toUpperCase()}
                </span>
                <button
                  onClick={() => setSplitEditorTextSize(prev => {
                    const sizes: ('sm' | 'base' | 'lg' | 'xl')[] = ['sm', 'base', 'lg', 'xl'];
                    const idx = sizes.indexOf(prev);
                    return idx < sizes.length - 1 ? sizes[idx + 1] : prev;
                  })}
                  disabled={splitEditorTextSize === 'xl'}
                  className={`p-1.5 rounded transition-colors ${
                    splitEditorTextSize === 'xl'
                      ? isDarkMode ? 'text-gray-600 cursor-not-allowed' : 'text-gray-300 cursor-not-allowed'
                      : isDarkMode ? 'text-gray-400 hover:bg-gray-700 hover:text-white' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                  }`}
                  title="Increase text size"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>

              <div className={`h-5 w-px ${isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`} />

              {/* Character count */}
              <div className="flex items-center gap-2">
                <span className={`text-sm ${
                  isOverLimit
                    ? 'text-red-500 font-medium'
                    : isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  {fullDescriptionCharCount.toLocaleString()} / {YOUTUBE_DESCRIPTION_LIMIT.toLocaleString()}
                </span>
                {isOverLimit && <AlertTriangle className="w-4 h-4 text-red-500" />}
              </div>

              {/* Save status */}
              <div className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded-md ${
                saveStatus === 'saving'
                  ? isDarkMode ? 'bg-[#314159] text-[#d9e2f1]' : 'bg-[#edf0f4] text-[#455468]'
                  : saveStatus === 'saved'
                    ? isDarkMode ? 'bg-[#223042] text-[#ffcf74]' : 'bg-[#232E3F] text-[#ffcf74]'
                    : saveStatus === 'error'
                      ? isDarkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-700'
                      : hasUnsavedChanges
                        ? isDarkMode ? 'bg-[#4b3913]/70 text-[#ffe09b]' : 'bg-[#fff1cc] text-[#6e4d0e]'
                        : isDarkMode ? 'bg-[#2d3749] text-gray-400' : 'bg-[#edf0f4] text-gray-500'
              }`}>
                {saveStatus === 'saving' ? (
                  <>
                    <Loader2 className="w-3 h-3 animate-spin" />
                    <span>Saving</span>
                  </>
                ) : saveStatus === 'saved' ? (
                  <>
                    <Cloud className="w-3 h-3" />
                    <span>Saved</span>
                  </>
                ) : saveStatus === 'error' ? (
                  <>
                    <CloudOff className="w-3 h-3" />
                    <span>Error</span>
                  </>
                ) : hasUnsavedChanges ? (
                  <>
                    <Cloud className="w-3 h-3" />
                    <span>Unsaved</span>
                  </>
                ) : (
                  <>
                    <Cloud className="w-3 h-3" />
                    <span>Synced</span>
                  </>
                )}
              </div>

              {/* Close button */}
              <button
                onClick={() => setShowSplitEditor(false)}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode
                    ? 'hover:bg-gray-700 text-gray-400 hover:text-white'
                    : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                }`}
                title="Close split editor (Esc)"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* LLM Tools Bar */}
          <div className={`flex-shrink-0 px-4 py-2 border-b flex items-center gap-2 flex-wrap ${
            isDarkMode ? 'bg-[#1a2231] border-[#334055]' : 'bg-[#f4f4f4] border-[#d9dde4]'
          }`}>
            {/* Generate Description button (agent-based) */}
            <button
              onClick={handleGenerateSummary}
              disabled={isGeneratingSummary}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-medium text-sm transition-colors ${
                isGeneratingSummary
                  ? isDarkMode ? 'bg-[#314159] text-[#d9e2f1]' : 'bg-[#edf0f4] text-[#455468]'
                  : isDarkMode
                    ? 'bg-[#FFB52B] hover:bg-[#f0a61d] text-[#232E3F] disabled:bg-[#2d3749] disabled:text-gray-500'
                    : 'bg-[#FFB52B] hover:bg-[#f0a61d] text-[#232E3F] disabled:bg-gray-200 disabled:text-gray-400'
              } disabled:cursor-not-allowed`}
              title="Generate a YouTube description using the AI agent"
            >
              {isGeneratingSummary ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{agentProgress || 'Generating...'}</span>
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  <span>Generate Description</span>
                </>
              )}
            </button>

            {/* Error message */}
            {llmError && (
              <div className={`flex items-center gap-2 px-3 py-1 rounded-lg text-sm ${
                isDarkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-700'
              }`}>
                <AlertTriangle className="w-4 h-4" />
                {llmError}
                <button
                  onClick={() => setLlmError(null)}
                  className="ml-1 hover:opacity-70"
                >
                  ×
                </button>
              </div>
            )}
          </div>

          {/* Split Panes */}
          <div className={`flex-1 flex overflow-hidden ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
            {/* Left Pane - Editor */}
            <div className={`w-1/2 flex flex-col border-r ${isDarkMode ? 'border-gray-700' : 'border-gray-300'}`}>
              <div className={`flex-shrink-0 px-4 py-2 border-b ${
                isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-100 border-gray-200'
              }`}>
                <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Editor (Markdown)
                </span>
              </div>
              <div className="flex-1 p-4 overflow-hidden">
                <textarea
                  value={description}
                  onChange={handleDescriptionChange}
                  placeholder="Write your YouTube video description here...&#10;&#10;Markdown formatting is supported:&#10;- **bold** or *italic*&#10;- [links](url)&#10;- # Headers&#10;- - bullet lists"
                  className={`w-full h-full p-4 rounded-lg border resize-none font-mono ${
                    splitEditorTextSize === 'sm' ? 'text-sm' :
                    splitEditorTextSize === 'base' ? 'text-base' :
                    splitEditorTextSize === 'lg' ? 'text-lg' : 'text-xl'
                  } ${
                    isDarkMode
                      ? 'bg-[#131a25] border-[#40506a] text-gray-200 placeholder-gray-500 focus:border-[#ffcf74]'
                      : 'bg-white border-[#cfd5df] text-gray-800 placeholder-gray-400 focus:border-[#ffcf74]'
                  } focus:outline-none focus:ring-2 focus:ring-[#ffcf74]/20`}
                  autoFocus
                />
              </div>
            </div>

            {/* Right Pane - Preview */}
            <div className="w-1/2 flex flex-col">
              <div className={`flex-shrink-0 px-4 py-2 border-b ${
                isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-100 border-gray-200'
              }`}>
                <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Preview
                </span>
              </div>
              <div className="flex-1 p-4 overflow-auto">
                <div className={`prose max-w-none ${
                  splitEditorTextSize === 'sm' ? 'prose-sm' :
                  splitEditorTextSize === 'base' ? 'prose-base' :
                  splitEditorTextSize === 'lg' ? 'prose-lg' : 'prose-xl'
                } ${
                  isDarkMode
                    ? 'prose-invert prose-p:text-gray-300 prose-headings:text-white prose-a:text-[#ffcf74] prose-strong:text-white prose-li:text-gray-300'
                    : 'prose-p:text-gray-700 prose-headings:text-gray-900 prose-strong:text-gray-900 prose-li:text-gray-700'
                }`}>
                  {description ? (
                    <ReactMarkdown>{description}</ReactMarkdown>
                  ) : (
                    <span className={`italic ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      Start typing to see preview...
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DescriptionTab;
